import { useState, useCallback, useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Camera,
  Upload,
  FileImage,
  X,
  Loader2,
  Plus,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ImagePlus,
  CameraOff,
  RotateCcw,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface ScannedImage {
  id: string;
  file: File;
  previewUrl: string;
  pageNumber: number;
}

export interface ScanUploadProps {
  /** Called when images are ready for submission */
  onImagesReady: (images: ScannedImage[]) => void;
  /** Called when upload progress changes (0-100) */
  onProgressChange?: (progress: number) => void;
  /** Maximum number of pages allowed */
  maxPages?: number;
  /** Maximum file size in bytes (default 10MB) */
  maxFileSize?: number;
  /** Allowed MIME types */
  allowedTypes?: string[];
  /** Whether upload is in progress (disables controls) */
  isUploading?: boolean;
  /** Current upload progress (0-100) */
  uploadProgress?: number;
  /** Additional class name */
  className?: string;
}

type CaptureMode = "idle" | "camera" | "preview";

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const DEFAULT_ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const DEFAULT_MAX_PAGES = 10;

// Magic number signatures for file validation (security)
const FILE_SIGNATURES: Record<string, number[]> = {
  "image/jpeg": [0xff, 0xd8, 0xff],
  "image/png": [0x89, 0x50, 0x4e, 0x47],
  "image/webp": [0x52, 0x49, 0x46, 0x46], // RIFF header (WebP starts with RIFF)
};

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(): string {
  return `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function isValidFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type);
}

/**
 * Validates file magic numbers (first bytes) to prevent MIME type spoofing.
 * Returns a promise that resolves to true if file signature matches claimed type.
 */
async function validateFileMagicNumber(file: File): Promise<boolean> {
  const expectedSignature = FILE_SIGNATURES[file.type];
  if (!expectedSignature) {
    // Unknown type - cannot validate
    return false;
  }

  try {
    const headerSize = Math.max(...Object.values(FILE_SIGNATURES).map((s) => s.length));
    const headerBuffer = await file.slice(0, headerSize).arrayBuffer();
    const headerBytes = new Uint8Array(headerBuffer);

    // Check if file starts with expected signature
    const matches = expectedSignature.every((byte, index) => headerBytes[index] === byte);

    // For WebP, also verify "WEBP" appears at bytes 8-11
    if (file.type === "image/webp" && matches) {
      const webpMarker = [0x57, 0x45, 0x42, 0x50]; // "WEBP"
      return webpMarker.every((byte, index) => headerBytes[8 + index] === byte);
    }

    return matches;
  } catch {
    return false;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

// ============================================================================
// Sub-Components
// ============================================================================

interface CameraViewProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  onCapture: () => void;
  onCancel: () => void;
  isCameraReady: boolean;
  cameraError: string | null;
}

function CameraView({
  videoRef,
  onCapture,
  onCancel,
  isCameraReady,
  cameraError,
}: CameraViewProps) {
  return (
    <div className="relative bg-black rounded-lg overflow-hidden">
      {cameraError ? (
        <div className="flex flex-col items-center justify-center h-[300px] sm:h-[400px] text-white p-6 text-center">
          <CameraOff className="h-12 w-12 mb-4 text-muted-foreground" />
          <p className="text-lg font-medium mb-2">Camera Access Denied</p>
          <p className="text-sm text-muted-foreground mb-4">{cameraError}</p>
          <Button variant="outline" onClick={onCancel}>
            Go Back
          </Button>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-[300px] sm:h-[400px] object-cover"
          />
          {!isCameraReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={onCancel}
              className="bg-white/10 backdrop-blur border-white/20 text-white hover:bg-white/20"
            >
              Cancel
            </Button>
            <Button
              size="lg"
              onClick={onCapture}
              disabled={!isCameraReady}
              className="bg-white text-black hover:bg-white/90"
            >
              <Camera className="mr-2 h-5 w-5" />
              Capture
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

interface ImagePreviewProps {
  images: ScannedImage[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
}

function ImagePreview({
  images,
  currentIndex,
  onIndexChange,
  onRemove,
  onClear,
}: ImagePreviewProps) {
  const currentImage = images[currentIndex];

  if (!currentImage) return null;

  return (
    <div className="space-y-4">
      {/* Main preview image */}
      <div className="relative">
        <img
          src={currentImage.previewUrl}
          alt={`Scanned page ${currentImage.pageNumber}`}
          className="w-full max-h-[350px] sm:max-h-[400px] object-contain rounded-lg border bg-muted"
        />
        <Button
          variant="destructive"
          size="icon"
          className="absolute top-2 right-2"
          onClick={() => onRemove(currentImage.id)}
          aria-label="Remove this image"
        >
          <X className="h-4 w-4" />
        </Button>
        <Badge className="absolute top-2 left-2" variant="secondary">
          Page {currentImage.pageNumber} of {images.length}
        </Badge>
      </div>

      {/* File info */}
      <p className="text-sm text-muted-foreground text-center">
        {currentImage.file.name} ({formatFileSize(currentImage.file.size)})
      </p>

      {/* Navigation for multiple images */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onIndexChange(currentIndex - 1)}
            disabled={currentIndex === 0}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Thumbnail strip */}
          <div className="flex gap-1 overflow-x-auto max-w-[200px] sm:max-w-[300px] py-1">
            {images.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => onIndexChange(idx)}
                className={cn(
                  "w-10 h-10 sm:w-12 sm:h-12 rounded border-2 overflow-hidden flex-shrink-0 transition-all",
                  idx === currentIndex
                    ? "border-primary ring-2 ring-primary/30"
                    : "border-transparent hover:border-muted-foreground/50"
                )}
                aria-label={`View page ${img.pageNumber}`}
              >
                <img
                  src={img.previewUrl}
                  alt={`Page ${img.pageNumber} thumbnail`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => onIndexChange(currentIndex + 1)}
            disabled={currentIndex === images.length - 1}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Clear all button */}
      {images.length > 1 && (
        <div className="flex justify-center">
          <Button variant="ghost" size="sm" onClick={onClear} className="text-muted-foreground">
            <Trash2 className="mr-2 h-4 w-4" />
            Clear all ({images.length} pages)
          </Button>
        </div>
      )}
    </div>
  );
}

interface DropZoneProps {
  onFileSelect: (files: File[]) => void;
  isDragging: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  disabled?: boolean;
  allowMultiple?: boolean;
}

function DropZone({
  onFileSelect,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  disabled,
  allowMultiple,
}: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFileSelect(files);
    }
    // Reset input for re-selection
    e.target.value = "";
  };

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer transition-colors",
        isDragging
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/25 hover:border-primary/50",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      onClick={handleClick}
      onDragOver={disabled ? undefined : onDragOver}
      onDragLeave={disabled ? undefined : onDragLeave}
      onDrop={disabled ? undefined : onDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Drop zone for worksheet images"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={DEFAULT_ALLOWED_TYPES.join(",")}
        multiple={allowMultiple}
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
        aria-hidden="true"
      />
      <FileImage className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-muted-foreground/50 mb-4" />
      <p className="text-base sm:text-lg font-medium mb-1">
        {allowMultiple ? "Drop your worksheet images here" : "Drop your worksheet image here"}
      </p>
      <p className="text-sm text-muted-foreground mb-4">or tap to browse files</p>
      <p className="text-xs text-muted-foreground">
        Supports JPEG, PNG, WebP up to 10 MB
        {allowMultiple && " (multiple files)"}
      </p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ScanUpload({
  onImagesReady,
  onProgressChange,
  maxPages = DEFAULT_MAX_PAGES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  isUploading = false,
  uploadProgress = 0,
  className,
}: ScanUploadProps) {
  // State
  const [images, setImages] = useState<ScannedImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [captureMode, setCaptureMode] = useState<CaptureMode>("idle");
  const [isDragging, setIsDragging] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Track images for cleanup effect to avoid stale closure
  const imagesRef = useRef<ScannedImage[]>([]);

  // Keep imagesRef in sync with images state
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Cleanup camera stream on unmount or mode change
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);
    setCameraError(null);
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    setCaptureMode("camera");
    setCameraError(null);
    setIsCameraReady(false);

    try {
      // Request camera with preference for rear camera (environment) on mobile
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setIsCameraReady(true);
        };
      }
    } catch (err) {
      console.error("Camera access error:", err);
      let errorMessage = "Could not access camera.";
      if (err instanceof Error) {
        if (err.name === "NotAllowedError") {
          errorMessage = "Camera permission denied. Please allow camera access in your browser settings.";
        } else if (err.name === "NotFoundError") {
          errorMessage = "No camera found on this device.";
        } else if (err.name === "NotReadableError") {
          errorMessage = "Camera is in use by another application.";
        }
      }
      setCameraError(errorMessage);
    }
  }, []);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !isCameraReady) return;

    const video = videoRef.current;

    // Create canvas if not exists
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        // Create file from blob
        const timestamp = Date.now();
        const file = new File([blob], `scan_${timestamp}.jpg`, { type: "image/jpeg" });

        // Create preview URL
        const previewUrl = URL.createObjectURL(blob);

        // Add to images
        const newImage: ScannedImage = {
          id: generateId(),
          file,
          previewUrl,
          pageNumber: images.length + 1,
        };

        setImages((prev) => [...prev, newImage]);
        setCurrentIndex(images.length); // Navigate to new image

        // Stop camera and show preview
        stopCamera();
        setCaptureMode("preview");
      },
      "image/jpeg",
      0.92 // Quality
    );
  }, [isCameraReady, images.length, stopCamera]);

  // Cancel camera mode
  const cancelCamera = useCallback(() => {
    stopCamera();
    setCaptureMode(images.length > 0 ? "preview" : "idle");
  }, [stopCamera, images.length]);

  // Handle file selection (from drop zone or file input)
  const handleFileSelect = useCallback(
    async (files: File[]) => {
      // Clear previous validation errors
      setValidationErrors([]);

      const validFiles: File[] = [];
      const errors: string[] = [];

      for (const file of files) {
        // Check remaining capacity
        if (images.length + validFiles.length >= maxPages) {
          errors.push(`Maximum ${maxPages} pages allowed`);
          break;
        }

        // Validate MIME type (basic check)
        if (!isValidFileType(file, allowedTypes)) {
          errors.push(`${file.name}: Invalid file type. Allowed: JPEG, PNG, WebP`);
          continue;
        }

        // Validate size
        if (file.size > maxFileSize) {
          errors.push(`${file.name}: File too large (max ${formatFileSize(maxFileSize)})`);
          continue;
        }

        // Validate magic number (security - prevent MIME spoofing)
        const isValidSignature = await validateFileMagicNumber(file);
        if (!isValidSignature) {
          errors.push(`${file.name}: File content does not match file type`);
          continue;
        }

        validFiles.push(file);
      }

      // Display validation errors to user
      if (errors.length > 0) {
        setValidationErrors(errors);
      }

      if (validFiles.length === 0) return;

      // Create image objects
      const newImages: ScannedImage[] = validFiles.map((file, idx) => ({
        id: generateId(),
        file,
        previewUrl: URL.createObjectURL(file),
        pageNumber: images.length + idx + 1,
      }));

      setImages((prev) => [...prev, ...newImages]);
      setCurrentIndex(images.length); // Navigate to first new image
      setCaptureMode("preview");
    },
    [images.length, maxPages, maxFileSize, allowedTypes]
  );

  // Remove a single image
  const handleRemove = useCallback(
    (id: string) => {
      // Find the image to remove BEFORE updating state (avoids race condition)
      const removedImage = imagesRef.current.find((img) => img.id === id);
      const currentLength = imagesRef.current.length;

      // Revoke URL immediately
      if (removedImage) {
        URL.revokeObjectURL(removedImage.previewUrl);
      }

      // Update images state - handle all dependent logic in the updater
      setImages((prev) => {
        const newImages = prev.filter((img) => img.id !== id);
        // Renumber pages
        return newImages.map((img, idx) => ({ ...img, pageNumber: idx + 1 }));
      });

      // Adjust current index if needed (use captured length)
      setCurrentIndex((prev) => Math.max(0, Math.min(prev, currentLength - 2)));

      // Return to idle if no images left (use captured length)
      if (currentLength <= 1) {
        setCaptureMode("idle");
      }
    },
    [] // No dependencies needed - uses imagesRef for current state
  );

  // Clear all images
  const handleClear = useCallback(() => {
    // Revoke all URLs using ref for current state
    imagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setImages([]);
    setCurrentIndex(0);
    setCaptureMode("idle");
    setValidationErrors([]);
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [handleFileSelect]
  );

  // Notify parent when images change
  useEffect(() => {
    onImagesReady(images);
  }, [images, onImagesReady]);

  // Cleanup on unmount - use imagesRef to access current images (avoids stale closure)
  useEffect(() => {
    return () => {
      stopCamera();
      // Revoke all preview URLs using ref (not stale state)
      imagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [stopCamera]);

  // Progress callback
  useEffect(() => {
    onProgressChange?.(uploadProgress);
  }, [uploadProgress, onProgressChange]);

  const canAddMore = images.length < maxPages;

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Scan Worksheets
        </CardTitle>
        <CardDescription>
          Take photos or upload images of completed worksheets
          {maxPages > 1 && ` (up to ${maxPages} pages)`}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="text-sm">
                    {error}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Camera Mode */}
        {captureMode === "camera" && (
          <CameraView
            videoRef={videoRef}
            onCapture={capturePhoto}
            onCancel={cancelCamera}
            isCameraReady={isCameraReady}
            cameraError={cameraError}
          />
        )}

        {/* Preview Mode */}
        {captureMode === "preview" && images.length > 0 && (
          <>
            <ImagePreview
              images={images}
              currentIndex={currentIndex}
              onIndexChange={setCurrentIndex}
              onRemove={handleRemove}
              onClear={handleClear}
            />

            {/* Add more pages */}
            {canAddMore && !isUploading && (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={startCamera}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Add Page (Camera)
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = allowedTypes.join(",");
                    input.multiple = true;
                    input.onchange = (e) => {
                      const files = Array.from((e.target as HTMLInputElement).files || []);
                      if (files.length > 0) {
                        handleFileSelect(files);
                      }
                    };
                    input.click();
                  }}
                >
                  <ImagePlus className="mr-2 h-4 w-4" />
                  Add Page (File)
                </Button>
              </div>
            )}
          </>
        )}

        {/* Idle Mode - Show drop zone */}
        {captureMode === "idle" && (
          <>
            <DropZone
              onFileSelect={handleFileSelect}
              isDragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              disabled={isUploading}
              allowMultiple={maxPages > 1}
            />

            {/* Camera button for mobile */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={startCamera}
                disabled={isUploading}
              >
                <Camera className="mr-2 h-4 w-4" />
                Use Camera
              </Button>
            </div>
          </>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Uploading...</span>
              <span className="font-medium">{Math.round(uploadProgress)}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* Status bar */}
        {images.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
            <span>
              {images.length} page{images.length !== 1 ? "s" : ""} selected
            </span>
            {!isUploading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-auto py-1"
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Start over
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
